import { analytics } from '@/shared/services/analytics'
import {
  UserPermission,
  type UserFieldsFragment,
} from '@/shared/graphql/legacy/generated'
import { isSignedIn as isUserSignedIn } from '@dacr/auth'
import { defineStore } from 'pinia'
import { ref } from 'vue'

const PRO_GRANT_KEY = 'dacrPendingProGrant'
const PRO_GRANT_TTL = 5 * 60 * 1000

const readStoredProGrant = () => {
  const expiresAt = Number(localStorage.getItem(PRO_GRANT_KEY))

  if (!expiresAt || Number.isNaN(expiresAt) || expiresAt <= Date.now()) {
    localStorage.removeItem(PRO_GRANT_KEY)
    return 0
  }

  return expiresAt
}

export const useUserStore = defineStore('user', () => {
  const user = ref<UserFieldsFragment | null>(null)
  const isSignedIn = ref(isUserSignedIn())
  const pendingProGrant = ref(readStoredProGrant())

  const markProGrantPending = () => {
    const expiresAt = Date.now() + PRO_GRANT_TTL

    localStorage.setItem(PRO_GRANT_KEY, String(expiresAt))
    pendingProGrant.value = expiresAt
  }

  const clearProGrantPending = () => {
    localStorage.removeItem(PRO_GRANT_KEY)
    pendingProGrant.value = 0
  }

  const withPendingProGrant = (userData: UserFieldsFragment) => {
    if (!pendingProGrant.value) return userData

    if (userData.permissions.includes(UserPermission.Pro)) {
      clearProGrantPending()
      return userData
    }

    if (pendingProGrant.value <= Date.now()) {
      clearProGrantPending()
      return userData
    }

    return {
      ...userData,
      permissions: [...userData.permissions, UserPermission.Pro],
    }
  }

  const setUser = (userData: UserFieldsFragment) => {
    const resolved = withPendingProGrant(userData)

    user.value = resolved
    localStorage.setItem('dacrUser', JSON.stringify(resolved))

    analytics.identify(resolved.id, {
      name: resolved.name,
      email: resolved.email,
      username: resolved.username,
      isPro: resolved.permissions.includes(UserPermission.Pro),
    })
  }

  const updateUser = (userData: Partial<UserFieldsFragment>) => {
    if (!user.value) return
    user.value = { ...user.value, ...userData }
  }

  const reset = () => {
    user.value = null
    isSignedIn.value = false
    clearProGrantPending()
  }

  return {
    user,
    setUser,
    updateUser,
    reset,
    isSignedIn,
    pendingProGrant,
    markProGrantPending,
    clearProGrantPending,
  }
})
